import type { CustomerBalanceResponse } from '@/actions/customerBalance';
import type { PersonalFormData } from '@/app/(main)/checkout/_utils/common';
import { create, persist } from '@/libs';
import { CheckoutStep } from '@/types/enums';
import type { Address } from '@/types/models/Address';
import type { PurchaseRequest, PurchaseResponse } from '@/types/models/Purchase';
import type { PurchasedRaffle } from '@/types/models/PurchasedRaffle';
import { logger } from '@/utils';

export type UtmData = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

export type AffiliateData = {
  soldById: string;
  capturedAt: string;
};

export type CheckoutData = {
  personalData: PersonalFormData | null;
  addressData: Address | null;
  purchaseData: Partial<PurchaseRequest> | null;
  paymentData: PurchaseResponse | null;
  purchasedRaffles: PurchasedRaffle[] | null;
  customerBalance: CustomerBalanceResponse | null;
  affiliateData: AffiliateData | null;
  utmData: UtmData | null;
  paymentExpiresAt: number | null;
  currentStep: CheckoutStep;
  isProcessing: boolean;
  error: string | null;
  paymentTimer: {
    isActive: boolean;
    timeRemaining: number;
    formatted: string;
  };
};

type CheckoutStore = {
  checkoutData: CheckoutData;
  setPersonalData: (data: PersonalFormData) => void;
  clearPersonalData: () => void;
  setAddressData: (data: Address) => void;
  clearAddressData: () => void;
  setPurchaseData: (data: Partial<PurchaseRequest>) => void;
  updatePurchaseData: (data: Partial<PurchaseRequest>) => void;
  clearPurchaseData: () => void;
  setPaymentData: (data: PurchaseResponse) => void;
  clearPaymentData: () => void;
  setPaymentExpiresAt: (expiresAt: number | null) => void;
  setPurchasedRaffles: (data: PurchasedRaffle[]) => void;
  clearPurchasedRaffles: () => void;
  setCustomerBalance: (data: CustomerBalanceResponse) => void;
  clearCustomerBalance: () => void;
  setAffiliateData: (data: AffiliateData) => void;
  clearAffiliateData: () => void;
  setUtmData: (data: UtmData) => void;
  clearUtmData: () => void;
  setCurrentStep: (step: CheckoutStep) => void;
  nextStep: (expectedFrom?: CheckoutStep) => void;
  previousStep: () => void;
  resetCheckout: () => void;
  setProcessing: (isProcessing: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  startPaymentTimer: (duration: number) => void;
  updatePaymentTimer: (timeRemaining: number, formatted: string) => void;
  stopPaymentTimer: () => void;
  proceedToPayment: (personalData: PersonalFormData) => void;
  proceedToCompleted: (paymentData: PurchaseResponse) => void;
};

const shouldGoToUpsell = (purchaseData: Partial<PurchaseRequest> | null): boolean => {
  if (purchaseData?.isPostGameUpsell) return false;

  const offers = purchaseData?.promotionOffers;

  if (offers) {
    return offers.some((offer) => offer.type === 'DURING_ORDER' && offer.quantity > 0);
  }

  const rawTicketPrice = purchaseData?.ticketPrice;
  const ticketPrice =
    typeof rawTicketPrice === 'number' ? rawTicketPrice : Number(rawTicketPrice) || 0;

  return Boolean(purchaseData?.isPromotionAvailable && ticketPrice > 0);
};

export const QR_CODE_TIMER = 5 * 60 * 1000;

const initialCheckoutData: CheckoutData = {
  personalData: null,
  addressData: null,
  purchaseData: null,
  paymentData: null,
  purchasedRaffles: null,
  customerBalance: null,
  affiliateData: null,
  utmData: null,
  paymentExpiresAt: null,
  currentStep: CheckoutStep.FORM,
  isProcessing: false,
  error: null,
  paymentTimer: {
    isActive: false,
    timeRemaining: 0,
    formatted: '00:00',
  },
};

export const useCheckoutStore = create<CheckoutStore>()(
  persist(
    (set, get) => ({
      checkoutData: initialCheckoutData,

      setPersonalData: (data) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            personalData: data,
          },
        }));
      },

      clearPersonalData: () => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            personalData: null,
          },
        }));
      },

      setAddressData: (data) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            addressData: data,
          },
        }));
      },

      clearAddressData: () => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            addressData: null,
          },
        }));
      },

      setPurchaseData: (data) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            purchaseData: data,
          },
        }));
      },

      updatePurchaseData: (data) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            purchaseData: {
              ...state.checkoutData.purchaseData,
              ...data,
            },
          },
        }));
      },

      clearPurchaseData: () => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            purchaseData: null,
          },
        }));
      },

      setPaymentData: (data) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            paymentData: data,
            paymentExpiresAt: data?.qrCode ? Date.now() + QR_CODE_TIMER : null,
          },
        }));
      },

      clearPaymentData: () => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            paymentData: null,
            paymentExpiresAt: null,
          },
        }));
      },

      setPaymentExpiresAt: (expiresAt) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            paymentExpiresAt: expiresAt,
          },
        }));
      },

      setPurchasedRaffles: (data) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            purchasedRaffles: data,
          },
        }));
      },

      clearPurchasedRaffles: () => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            purchasedRaffles: null,
          },
        }));
      },

      setCustomerBalance: (data) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            customerBalance: data,
          },
        }));
      },

      clearCustomerBalance: () => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            customerBalance: null,
          },
        }));
      },

      setAffiliateData: (data) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            affiliateData: data,
          },
        }));
      },

      clearAffiliateData: () => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            affiliateData: null,
          },
        }));
      },

      setUtmData: (data) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            utmData: data,
          },
        }));
      },

      clearUtmData: () => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            utmData: null,
          },
        }));
      },

      setCurrentStep: (step) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            currentStep: step,
          },
        }));
      },

      nextStep: (expectedFrom?: CheckoutStep) => {
        const { checkoutData } = get();
        const { currentStep, purchaseData } = checkoutData;

        if (expectedFrom !== undefined && expectedFrom !== currentStep) {
          logger.warn(
            `[checkout] nextStep blocked: expected "${expectedFrom}" but current is "${currentStep}"`,
          );
          return;
        }

        if (currentStep === CheckoutStep.FORM) {
          const next = shouldGoToUpsell(purchaseData)
            ? CheckoutStep.UPSELL
            : CheckoutStep.PURCHASE;

          set((state) => ({
            checkoutData: {
              ...state.checkoutData,
              currentStep: next,
            },
          }));
          return;
        }

        if (currentStep === CheckoutStep.UPSELL) {
          const next = CheckoutStep.PURCHASE;
          set((state) => ({
            checkoutData: {
              ...state.checkoutData,
              currentStep: next,
            },
          }));
          return;
        }

        if (currentStep === CheckoutStep.PURCHASE) {
          const next = CheckoutStep.COMPLETED;
          set((state) => ({
            checkoutData: {
              ...state.checkoutData,
              currentStep: next,
            },
          }));
        }
      },

      previousStep: () => {
        const { currentStep } = get().checkoutData;

        if (currentStep === CheckoutStep.UPSELL) {
          const prev = CheckoutStep.FORM;
          set((state) => ({
            checkoutData: {
              ...state.checkoutData,
              currentStep: prev,
            },
          }));
          return;
        }

        if (currentStep === CheckoutStep.PURCHASE) {
          const { purchaseData } = get().checkoutData;
          const prev = shouldGoToUpsell(purchaseData)
            ? CheckoutStep.UPSELL
            : CheckoutStep.FORM;

          set((state) => ({
            checkoutData: {
              ...state.checkoutData,
              currentStep: prev,
            },
          }));
          return;
        }

        if (currentStep === CheckoutStep.COMPLETED) {
          const prev = CheckoutStep.PURCHASE;
          set((state) => ({
            checkoutData: {
              ...state.checkoutData,
              currentStep: prev,
            },
          }));
        }
      },

      resetCheckout: () => {
        set({ checkoutData: initialCheckoutData });
      },

      setProcessing: (isProcessing) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            isProcessing,
          },
        }));
      },

      setError: (error) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            error,
          },
        }));
      },

      clearError: () => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            error: null,
          },
        }));
      },

      startPaymentTimer: (duration) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            paymentTimer: {
              isActive: true,
              timeRemaining: duration,
              formatted: formatTime(duration),
            },
          },
        }));
      },

      updatePaymentTimer: (timeRemaining, formatted) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            paymentTimer: {
              ...state.checkoutData.paymentTimer,
              timeRemaining,
              formatted,
            },
          },
        }));
      },

      stopPaymentTimer: () => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            paymentTimer: {
              isActive: false,
              timeRemaining: 0,
              formatted: '00:00',
            },
          },
        }));
      },

      proceedToPayment: (personalData) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            personalData,
            currentStep: CheckoutStep.PURCHASE,
            error: null,
          },
        }));
      },

      proceedToCompleted: (paymentData) => {
        set((state) => ({
          checkoutData: {
            ...state.checkoutData,
            paymentData,
            paymentExpiresAt: null,
            currentStep: CheckoutStep.COMPLETED,
            isProcessing: false,
            error: null,
            paymentTimer: {
              isActive: false,
              timeRemaining: 0,
              formatted: '00:00',
            },
          },
        }));
      },
    }),
    {
      name: 'checkout-store',
      partialize: (state) => ({
        checkoutData: {
          ...state.checkoutData,
          affiliateData: null,
          isProcessing: false,
          error: null,
          paymentTimer: {
            isActive: false,
            timeRemaining: 0,
            formatted: '00:00',
          },
        },
      }),
    },
  ),
);

function formatTime(milliseconds: number): string {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}
